import React, { useState, useEffect, useCallback } from 'react';
import { _T, C } from '../../shared/theme';

const BASE = process.env.REACT_APP_API_URL || '/api';

const COACH_PACKS = [
  { id: 'coach_10k',  label: '10,000',     price: '€89.99',    credits: 10000 },
  { id: 'coach_50k',  label: '50,000',     price: '€349.99',   credits: 50000,  popular: true },
  { id: 'coach_500k', label: '500,000',    price: '€2,499',    credits: 500000 },
  { id: 'coach_1m',   label: '1,000,000',  price: '€3,999',    credits: 1000000 },
];

export default function CoachWalletTab({ coachId, clients, lang }) {
  const nl = lang === 'nl';
  const [balance, setBalance] = useState(0);
  const [txns, setTxns]       = useState(null);
  const [buying, setBuying]   = useState(null);
  const [buyError, setBuyError] = useState(null);
  const [giftState, setGiftState] = useState({}); // { clientId: { amount, loading, error } }
  const accent = _T.accent;

  const load = useCallback(() => {
    if (!coachId) return;
    fetch(`${BASE}/wallet/${encodeURIComponent(coachId)}`)
      .then(r => r.json())
      .then(data => {
        if (data.balance !== undefined) setBalance(data.balance);
        setTxns(data.transactions || []);
      })
      .catch(() => setTxns([]));
  }, [coachId]);

  useEffect(() => { load(); }, [load]);

  const buyPack = async (packId) => {
    setBuying(packId);
    setBuyError(null);
    try {
      const res = await fetch(`${BASE}/stripe/credits-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: coachId, packId }),
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; }
      else setBuyError(data.error || (nl ? 'Betaling mislukt.' : 'Payment failed.'));
    } catch { setBuyError(nl ? 'Verbindingsfout.' : 'Connection error.'); }
    finally { setBuying(null); }
  };

  const giftCredits = async (clientId) => {
    const state = giftState[clientId] || {};
    const amount = parseInt(state.amount || '0', 10);
    if (!amount || amount <= 0) return;
    setGiftState(prev => ({ ...prev, [clientId]: { ...prev[clientId], loading: true, error: null } }));
    try {
      const res = await fetch(`${BASE}/wallet/${encodeURIComponent(coachId)}/gift`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_id: clientId, amount }),
      });
      const data = await res.json();
      if (data.ok) {
        setBalance(data.balance);
        setGiftState(prev => ({ ...prev, [clientId]: { amount: '', loading: false, error: null, done: true } }));
        setTimeout(() => setGiftState(prev => ({ ...prev, [clientId]: { amount: '' } })), 2000);
        load();
      } else {
        setGiftState(prev => ({ ...prev, [clientId]: { ...prev[clientId], loading: false, error: data.error || 'Error' } }));
      }
    } catch {
      setGiftState(prev => ({ ...prev, [clientId]: { ...prev[clientId], loading: false, error: nl ? 'Fout' : 'Error' } }));
    }
  };

  return (
    <div style={{ paddingBottom: 32 }}>
      {/* Balance */}
      <div style={{
        background: `linear-gradient(135deg, ${accent}22, ${_T.grad2}22)`,
        border: `1px solid ${accent}44`, borderRadius: 20, padding: '24px 20px',
        textAlign: 'center', marginBottom: 20,
      }}>
        <div style={{ fontSize: 10, color: accent, letterSpacing: 3, fontWeight: 700, marginBottom: 6 }}>
          {nl ? 'COACH CREDITS' : 'COACH CREDITS'}
        </div>
        <div style={{ fontSize: 46, fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: -1 }}>
          {balance.toLocaleString()}
        </div>
        <div style={{ fontSize: 12, color: C.silver, marginTop: 4 }}>
          {nl ? 'beschikbare credits om te schenken' : 'available credits to gift'}
        </div>
      </div>

      {/* Buy packs */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, fontWeight: 700, marginBottom: 12 }}>
          {nl ? 'CREDITS KOPEN' : 'BUY CREDITS'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {COACH_PACKS.map(pack => (
            <button
              key={pack.id}
              onClick={() => buyPack(pack.id)}
              disabled={!!buying}
              style={{
                position: 'relative',
                background: pack.popular ? `${accent}33` : 'rgba(255,255,255,0.05)',
                border: `1px solid ${pack.popular ? accent + '88' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 14, padding: '14px 10px', cursor: buying ? 'not-allowed' : 'pointer',
                opacity: buying && buying !== pack.id ? 0.6 : 1, textAlign: 'center',
              }}
            >
              {pack.popular && (
                <div style={{ position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)', background: accent, color: '#0a1220', fontSize: 8, fontWeight: 800, letterSpacing: 1.5, padding: '2px 8px', borderRadius: 20 }}>
                  {nl ? 'POPULAIR' : 'POPULAR'}
                </div>
              )}
              <div style={{ fontSize: 13, fontWeight: 700, color: pack.popular ? accent : '#fff', marginBottom: 3 }}>
                {pack.label}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>
                {buying === pack.id ? '...' : pack.price}
              </div>
              <div style={{ fontSize: 10, color: C.silver, marginTop: 2 }}>credits</div>
            </button>
          ))}
        </div>
        {buyError && <div style={{ color: '#C05050', fontSize: 11, textAlign: 'center', marginTop: 10 }}>{buyError}</div>}
      </div>

      {/* Gift to clients */}
      {clients.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, fontWeight: 700, marginBottom: 12 }}>
            {nl ? 'CREDITS SCHENKEN AAN KLANTEN' : 'GIFT CREDITS TO CLIENTS'}
          </div>
          {clients.map(client => {
            const gs = giftState[client.id] || {};
            return (
              <div key={client.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', marginBottom: 8,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 14,
              }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${accent}20`, border: `2px solid ${accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: accent, flexShrink: 0 }}>
                  {(client.name || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.name}</div>
                  {gs.done && <div style={{ color: '#4AC87A', fontSize: 10 }}>{nl ? '✓ Verstuurd!' : '✓ Sent!'}</div>}
                  {gs.error && <div style={{ color: '#C05050', fontSize: 10 }}>{gs.error}</div>}
                </div>
                <input
                  type="number"
                  min="1"
                  value={gs.amount || ''}
                  onChange={e => setGiftState(prev => ({ ...prev, [client.id]: { ...prev[client.id], amount: e.target.value, done: false } }))}
                  placeholder="0"
                  style={{
                    width: 64, background: _T.bg4, border: `1px solid rgba(255,255,255,0.15)`,
                    borderRadius: 8, color: '#fff', fontSize: 13, padding: '6px 8px', textAlign: 'center',
                    outline: 'none',
                  }}
                />
                <button
                  onClick={() => giftCredits(client.id)}
                  disabled={!gs.amount || gs.loading}
                  style={{
                    background: `linear-gradient(135deg,${accent},${_T.grad2})`,
                    border: 'none', borderRadius: 8, color: '#0a1220',
                    fontSize: 11, fontWeight: 700, padding: '6px 12px',
                    cursor: gs.amount && !gs.loading ? 'pointer' : 'not-allowed',
                    opacity: gs.amount && !gs.loading ? 1 : 0.5,
                  }}
                >
                  {gs.loading ? '...' : (nl ? 'Stuur' : 'Send')}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Recent transactions */}
      <div>
        <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, fontWeight: 700, marginBottom: 12 }}>
          {nl ? 'TRANSACTIES' : 'TRANSACTIONS'}
        </div>
        {txns === null ? (
          <div style={{ color: C.silver, fontSize: 13, textAlign: 'center', padding: '16px 0' }}>{nl ? 'Laden...' : 'Loading...'}</div>
        ) : txns.length === 0 ? (
          <div style={{ color: C.silver, fontSize: 13, textAlign: 'center', padding: '16px 0' }}>{nl ? 'Nog geen transacties.' : 'No transactions yet.'}</div>
        ) : (
          txns.slice(0, 15).map((tx, i) => {
            const isPos = tx.amount > 0;
            const reasonMap = {
              coach_gift_out:  { icon: '🎁', label: nl ? 'Cadeau verstuurd' : 'Gift sent' },
              credit_purchase: { icon: '💳', label: nl ? 'Aankoop' : 'Purchase' },
              monthly_grant:   { icon: '🔄', label: nl ? 'Maandelijks' : 'Monthly' },
              activation_grant:{ icon: '🚀', label: nl ? 'Welkomstbonus' : 'Welcome bonus' },
            };
            const { icon, label } = reasonMap[tx.reason] || { icon: '💠', label: tx.reason };
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < Math.min(txns.length, 15) - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: isPos ? 'rgba(74,200,122,0.12)' : 'rgba(255,100,100,0.1)', border: `1px solid ${isPos ? '#4AC87A33' : '#C0505033'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                  {icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{label}</div>
                  {tx.ref_id && <div style={{ color: C.silver, fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.ref_id}</div>}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: isPos ? '#4AC87A' : '#FF6464' }}>
                  {isPos ? '+' : ''}{tx.amount.toLocaleString()}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
